/**
 * SubTabs — sous-navigation horizontale en « pills ».
 *
 * Remplace les menus de sous-navigation LATÉRAUX (colonne de gauche), qui
 * coûtaient 234 px (210 de menu + 24 de gouttière) sur un conteneur `max-w-6xl`
 * de 1152 px, soit 20 % de la largeur utile, en permanence, sur chaque écran.
 * Ici le coût est de ~46 px de hauteur, une seule fois en haut de page — et la
 * hauteur se récupère au scroll, contrairement à la largeur.
 *
 * Choix de la disposition validé le 2026-07-25 (cf. `spec/cockpit-manager.md`) :
 * tient de 2 à 6+ onglets sans changer de règle, garde les libellés lisibles en
 * entier, défile horizontalement quand l'écran est trop étroit.
 *
 * ⚠️ Ne PAS l'utiliser pour un sommaire de document long à scroll-spy
 * (HelpPage, AdminTechDocPage, ChallengeBriefPage) : là, la colonne sticky
 * indique la position dans le document, ce n'est pas de la navigation entre vues.
 */
import { useEffect, useRef, type ReactNode } from "react";

export interface SubTabItem {
  id: string;
  /** Libellé déjà traduit (l'appelant fait le t()). */
  label: string;
  /** Emoji, icône lucide, ou rien. */
  icon?: ReactNode;
  /** Valeur de l'attribut `data-tour` (visites guidées). Défaut : `sub-nav-{id}`. */
  tourId?: string;
}

interface SubTabsProps {
  items: SubTabItem[];
  activeId: string | undefined;
  onSelect: (id: string) => void;
  /** Couleur de la casquette active (cf. `ENCADRANT_ROLE_META`). Défaut : bleu Kestio. */
  accent?: string;
  /** Libellé du groupe pour les lecteurs d'écran (ex. « Concevoir »). */
  ariaLabel?: string;
  className?: string;
}

export function SubTabs({
  items,
  activeId,
  onSelect,
  accent = "#384872",
  ariaLabel,
  className,
}: SubTabsProps) {
  // Sur écran étroit la barre défile : l'onglet actif doit rester visible, sinon on lit le
  // contenu d'un onglet dont on ne voit pas le titre. Défilement horizontal de la seule
  // barre — `scrollIntoView` ferait aussi défiler la page.
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const list = listRef.current;
    const active = list?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!list || !active) return;
    if (active.offsetLeft < list.scrollLeft || active.offsetLeft + active.offsetWidth > list.scrollLeft + list.clientWidth) {
      list.scrollLeft = active.offsetLeft - (list.clientWidth - active.offsetWidth) / 2;
    }
  }, [activeId]);

  if (items.length === 0) return null;

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      className={`relative mb-5 flex w-max max-w-full gap-1 overflow-x-auto rounded-full bg-[#F1F1F7] p-1 scrollbar-hide ${className ?? ""}`}
    >
      {items.map((item) => {
        const isActive = activeId === item.id;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-current={isActive}
            data-tour={item.tourId ?? `sub-nav-${item.id}`}
            onClick={() => onSelect(item.id)}
            className={`flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition ${
              isActive ? "bg-white shadow-[var(--elev-1)]" : "hover:text-[#384872]"
            }`}
            style={isActive ? { color: accent } : { color: "#5A6076" }}
          >
            {item.icon != null && (
              <span className="flex w-[18px] shrink-0 items-center justify-center text-base leading-none">
                {item.icon}
              </span>
            )}
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
